// src/pages/Login.js
import React, { useState, useRef } from "react";
import { supabase } from "../supabaseClient";
import { routes } from "../routes";
import bcrypt from "bcryptjs";
import { sendUniversalEmail } from "../utils/mailer";
import { useBranding } from "../context/BrandingContext";

/* ---------- Toast con fallback inline (local) ---------- */
function Toast({ toast, onClose }) {
  if (!toast) return null;

  const { branding } = useBranding();
  const brandColor = branding.colors.primary;

  const theme = {
    success: { barClass: "bg-green-500", barColor: "#22c55e", textClass: "text-green-700", textColor: "#15803d", ringClass: "ring-green-200" },
    error:   { barClass: "bg-red-500",   barColor: "#ef4444", textClass: "text-red-700",   textColor: "#b91c1c", ringClass: "ring-red-200"   },
    warning: { barClass: "bg-amber-500", barColor: "#f59e0b", textClass: "text-amber-700", textColor: "#b45309", ringClass: "ring-amber-200" },
    info:    { barClass: "bg-brand", barColor: brandColor, textClass: "text-brand", textColor: brandColor, ringClass: "ring-brand/30" },
  }[toast.variant || "info"];

  return (
    <div
      className="fixed top-4 right-4 z-[9999] w-[92vw] max-w-sm pointer-events-none"
      style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, width: '92vw', maxWidth: '24rem', pointerEvents: 'none' }}
    >
      <div
        role="status"
        aria-live="polite"
        className={`pointer-events-auto shadow-xl rounded-xl border border-gray-100 bg-white overflow-hidden ring-1 ${theme.ringClass} animate-[fadeIn_.18s_ease-out]`}
        style={{
          pointerEvents: 'auto',
          background: '#fff',
          border: '1px solid #eee',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.08)'
        }}
      >
        <div className="flex" style={{ display: 'flex' }}>
          <div className={`${theme.barClass} w-1`} style={{ backgroundColor: theme.barColor, width: 4 }} />
          <div className="flex-1 p-3" style={{ flex: 1, padding: 12 }}>
            <div className="flex items-start" style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div className={`mr-2 mt-0.5 ${theme.textClass}`} style={{ color: theme.textColor, marginRight: 8, marginTop: 2 }}>
                <strong>{toast.variant === 'error' ? '⚠' : toast.variant === 'warning' ? '!' : toast.variant === 'success' ? '✓' : 'i'}</strong>
              </div>
              <div className="flex-1" style={{ flex: 1 }}>
                {toast.title && <p className="text-sm font-semibold text-gray-900" style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>{toast.title}</p>}
                {toast.description && <p className="text-sm text-gray-600 mt-0.5" style={{ marginTop: 4, fontSize: 14, color: '#4b5563' }}>{toast.description}</p>}
              </div>
              <button
                onClick={onClose}
                aria-label="Cerrar"
                className="ml-2 text-gray-400 hover:text-gray-600"
                style={{ marginLeft: 8, color: '#9ca3af', background: 'transparent', border: 0, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            {toast.duration > 0 && (
              <div className="mt-3 h-1 w-full bg-gray-100 overflow-hidden rounded"
                   style={{ marginTop: 12, height: 4, width: '100%', background: '#f3f4f6', overflow: 'hidden', borderRadius: 4 }}>
                <div className={`${theme.barClass} h-full`} style={{ height: '100%', backgroundColor: theme.barColor, animation: `bar ${toast.duration}ms linear forwards` }} />
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes bar { from { width:100% } to { width:0% } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px);} to { opacity:1; transform:translateY(0);} }
      `}</style>
    </div>
  );
}
/* --------------------------------------------------------- */

/* ---------- Modal: Recuperar contraseña ---------- */
function ForgotPasswordModal({ open, onClose, onRequested }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const { branding } = useBranding();

  if (!open) return null;

  const genToken = () => {
    try {
      const arr = new Uint8Array(16);
      (window.crypto || crypto).getRandomValues(arr);
      return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // Fallback (menos seguro, pero evita romper UI si crypto no está)
      return Math.random().toString(16).slice(2) + Math.random().toString(16).slice(2);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      setSending(true);

      // 1) Buscar cliente por correo (no revelamos si no existe)
      const { data: cli } = await supabase
        .from("clientes")
        .select("id, nombre, correo")
        .eq("correo", email.trim())
        .maybeSingle();

      // Generar token (30 min)
      const token = genToken();
      const expiresAt = new Date(Date.now() + 30 * 60 * 1000).toISOString();

      if (cli?.id) {
        // 2) Guardar token en Supabase
        const { error: insErr } = await supabase.from("password_reset_tokens").insert([{
          cliente_id: cli.id,
          token,
          expires_at: expiresAt,
        }]);
        if (insErr) {
          console.error("No se pudo crear token:", insErr);
          onRequested?.({ ok: false, message: "No pudimos generar el enlace de recuperación." });
          setSending(false);
          return;
        }

        // 3) Enviar email con tu backend SMTP
        const resetUrl = `${window.location.origin}${routes.reset}?token=${encodeURIComponent(token)}`;
        try {
          await sendUniversalEmail({
            to_email: cli.correo,
            reply_to: "info@elancla.store",
            customer_name: cli.nombre || "Cliente",
            title: "Restablece tu contraseña",
            preheader: "Enlace para crear una nueva contraseña (expira en 30 minutos).",
            intro_html: 'Recibimos una solicitud para crear una nueva contraseña en <strong>El Ancla CR</strong>.',
            body_html: '<p style="margin:0 0 16px 0;color:#374151;">Por tu seguridad, este enlace <strong>expira en 30 minutos</strong>. Si no fuiste vos, podés ignorarlo.</p>',
            cta_label: "Crear nueva contraseña",
            cta_url: resetUrl,
          }, branding);
        } catch (mailErr) {
          console.error("Mail reset error:", mailErr);
          onRequested?.({ ok: false, message: "No pudimos enviar el correo de recuperación." });
          setSending(false);
          return;
        }
      }

      // Siempre respondemos éxito (para no revelar existencia del correo)
      onRequested?.({ ok: true, message: "Si el correo existe, te enviamos un enlace para crear una nueva contraseña." });
      setSending(false);
      onClose?.();
    } catch (err) {
      console.error(err);
      onRequested?.({ ok: false, message: "Ocurrió un problema al procesar la solicitud." });
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-black/20 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          aria-label="Cerrar"
          disabled={sending}
        >
          ×
        </button>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Recuperar contraseña</h3>
        <p className="text-gray-600 mb-4">
          Ingresa tu correo y te enviaremos un enlace para crear una nueva contraseña.
        </p>
        <form onSubmit={handleSend} className="space-y-4">
          <input
            type="email"
            className="w-full rounded-lg border-gray-300 focus:border-brand focus:ring-brand px-3 py-2"
            placeholder="tu@correo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              disabled={sending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={sending}
              className="px-4 py-2 rounded-lg bg-brand text-white hover:bg-brand/90 disabled:opacity-70 inline-flex items-center gap-2"
            >
              {sending && <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
              {sending ? "Enviando…" : "Enviar enlace"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
/* ------------------------------------------------------- */

export default function LoginPage({ onNavigate, onLoggedIn, brandLogo = "/logo-ancla.svg", onNotify }) {
  const [correo, setCorreo] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [mostrarPass, setMostrarPass] = useState(false);
  const [loading, setLoading] = useState(false);

  // Toast local
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const showToast = ({ variant = "info", title, description, duration = 4000 }) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ variant, title, description, duration });
    if (duration > 0) {
      timerRef.current = setTimeout(() => setToast(null), duration);
    }
  };

  // Modal recuperar
  const [forgotOpen, setForgotOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!correo.trim() || !contrasena.trim()) {
        showToast({ variant: "warning", title: "Datos incompletos", description: "Ingresa tu correo y contraseña." });
        setLoading(false);
        return;
      }

      // 1) Buscar cliente
      const { data: cli, error } = await supabase
        .from("clientes")
        .select("id, nombre, correo, contrasena_hash, activo")
        .eq("correo", correo.trim())
        .limit(1)
        .single();

      if (error || !cli) {
        showToast({ variant: "error", title: "No pudimos iniciar sesión", description: "Correo o contraseña incorrectos." });
        setLoading(false);
        return;
      }
      if (cli.activo === false) {
        showToast({ variant: "warning", title: "Cuenta inactiva", description: "Contáctanos para activarla." });
        setLoading(false);
        return;
      }

      // 2) Validar contraseña
      const ok = await bcrypt.compare(contrasena, cli.contrasena_hash);
      if (!ok) {
        showToast({ variant: "error", title: "No pudimos iniciar sesión", description: "Correo o contraseña incorrectos." });
        setLoading(false);
        return;
      }

      // 3) Persistir sesión
      const user = { id: cli.id, nombre: cli.nombre, correo: cli.correo };
      onLoggedIn?.(user);

      // 4) Bienvenida
      const firstName = (cli.nombre || "").trim().split(/\s+/)[0] || "¡Hola!";
      if (onNotify) {
        onNotify({
          variant: "success",
          title: `¡Bienvenido, ${firstName}!`,
          description: "Has iniciado sesión correctamente.",
          duration: 4500,
        });
      } else {
        showToast({
          variant: "success",
          title: `¡Bienvenido, ${firstName}!`,
          description: "Has iniciado sesión correctamente.",
          duration: 3000,
        });
      }

      // 5) Ir al home
      onNavigate?.(routes.home);
    } catch (err) {
      console.error(err);
      showToast({ variant: "error", title: "Error inesperado", description: "Ocurrió un problema al iniciar sesión." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto pt-8 pb-20">
        <div className="mt-6 bg-white/90 backdrop-blur rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-center text-gray-900">
            Inicia sesión en <span className="text-brand">El Ancla CR</span>
          </h1>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correo</label>
              <input
                type="email"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                required
                className="w-full rounded-lg border-gray-300 focus:border-brand focus:ring-brand px-3 py-2"
                placeholder="tu@correo.com"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
              <div className="relative">
                <input
                  type={mostrarPass ? "text" : "password"}
                  value={contrasena}
                  onChange={(e) => setContrasena(e.target.value)}
                  required
                  className="w-full rounded-lg border-gray-300 focus:border-brand focus:ring-brand px-3 py-2 pr-12"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setMostrarPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-brand hover:underline"
                >
                  {mostrarPass ? "Ocultar" : "Mostrar"}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand text-white py-3 rounded-lg font-semibold hover:bg-brand/90 transition-colors disabled:opacity-70"
            >
              {loading ? "Ingresando…" : "Ingresar"}
            </button>

            {/* Link recuperar */}
            <p className="text-center text-sm text-gray-600">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-brand font-semibold hover:underline mt-2"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </p>
          </form>

          <p className="text-center text-sm text-gray-600 mt-5">
            ¿No tienes cuenta?{" "}
            <button
              onClick={() => onNavigate?.(routes.registro)}
              className="text-brand font-semibold hover:underline"
            >
              Regístrate
            </button>
          </p>
        </div>
      </div>

      {/* Modal recuperar */}
      <ForgotPasswordModal
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        onRequested={(r) => {
          if (r?.ok) {
            (onNotify || showToast)({
              variant: "success",
              title: "Revisa tu correo",
              description: r.message || "Si el correo existe, te enviamos un enlace para crear una nueva contraseña.",
            });
          } else {
            (onNotify || showToast)({
              variant: "error",
              title: "No se pudo enviar",
              description: r?.message || "Intenta nuevamente.",
            });
          }
        }}
      />

      {/* Toast local */}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </section>
  );
}
